"use client"

import React, { useState, useEffect, useMemo } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { useNavigate, useParams } from "react-router-dom"
import DashboardLayout from "../../components/layout/DashboardLayout"
import Button from "../../components/common/Button"
import VideoShowPlayer from "../../components/common/VideoShowPlayer"
import StatusUpdatePopup from "../../components/common/StatusUpdatePopup"
import { getCourseById, updateCourseStatus, getCourseMembers } from "../../api/courseService"
import { ArrowLeft, CheckCircle, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, DollarSign, Edit2, Eye, Percent, Play, Search, Share2, Users, Image, Mail, Phone, Award, PlayCircle, Radio, ShoppingBag } from "lucide-react"

const CourseDetails: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState("Overview")
  const [course, setCourse] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showDescriptionModal, setShowDescriptionModal] = useState(false)
  const [showVideoPlayer, setShowVideoPlayer] = useState(false)
  const [mediaType, setMediaType] = useState<'image' | 'video' | 'unknown'>('unknown')
  const [showStatusPopup, setShowStatusPopup] = useState(false)
  const [, setIsUpdatingStatus] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [previewTimeout, setPreviewTimeout] = useState<NodeJS.Timeout | null>(null)
  const [members, setMembers] = useState<any[]>([])
  const [memberStats, setMemberStats] = useState({ totalMembers: 0, totalWatched: 0 })
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState<string | null>(null)
  const [membersLoaded, setMembersLoaded] = useState(false)
  const [memberSearch, setMemberSearch] = useState("")
  const [memberPage, setMemberPage] = useState(1)
  const [memberPageSize, setMemberPageSize] = useState(10)

  const tabs = ["Overview", "Coupons", "Affiliate", "Members list", "Manage"]

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true)
        const response = await getCourseById(id!)
        
        console.log("Course API Response:", response);
        
        if (response.status === 200 && response.data) {
          const courseData = response.data
          
          let thumbnail = courseData.thumbnail;
          let videoUrl = courseData.video_url || courseData.thumbnail;
          let status = courseData.PublishStatus ;
          let detectedMediaType: 'image' | 'video' | 'unknown' = 'unknown';
          
          // Detect media type based on videoUrl
          if (videoUrl) {
            if (videoUrl.includes('/stream/') || videoUrl.includes('.m3u8') || videoUrl.includes('.mp4') || videoUrl.includes('.mov') || videoUrl.includes('.avi') || videoUrl.includes('.mkv') || videoUrl.includes('.webm')) {
              detectedMediaType = 'video';
              console.log("Video URL detected:", videoUrl);
            } else if (videoUrl.startsWith('data:image') || 
                      videoUrl.includes('.jpg') || videoUrl.includes('.jpeg') || 
                      videoUrl.includes('.png') || videoUrl.includes('.gif') || videoUrl.includes('.webp')) {
              detectedMediaType = 'image';
             // console.log("Image URL detected:", videoUrl);
            } else if (!videoUrl.startsWith('http')) {
              // Base64 handling
              if (!thumbnail.startsWith('data:image')) {
                thumbnail = `data:image/jpeg;base64,${thumbnail}`;
              }
              detectedMediaType = 'image';
              console.log("Base64 image detected");
            } else {
              // If video_url exists and thumbnail exists, likely a video with thumbnail
              if (courseData.video_url && courseData.thumbnail && courseData.video_url !== courseData.thumbnail) {
                detectedMediaType = 'video';
                console.log("Video with separate thumbnail detected");
              } else {
                detectedMediaType = 'image';
                console.log("Unknown media type, treating as image");
              }
            }
          }
          
          setMediaType(detectedMediaType);
          
          setCourse({
            id: courseData._id,
            title: courseData.title,
            description: courseData.description || "No description provided",
            instructor: courseData.created_by?.name || "Unknown Instructor",
            updatedBy: courseData.updatedBy?.name || "N/A",
            updatedAt: courseData.updatedAt || null,
            thumbnail: thumbnail,
            videoUrl: videoUrl,
            status: status,
            metrics: {
              totalEarnings: courseData.totalEarnings || "₹0",
              purchasedUsers: courseData.purchasedUsers || 0,
              courseCompleted: courseData.courseCompleted || "0%",
              avgPurchaseRate: courseData.avgPurchaseRate || "0%",
            }
          })
          
          console.log("Final course data:", {
            mediaType: detectedMediaType,
            thumbnail: thumbnail,
            videoUrl: videoUrl,
            hasVideoUrl: !!courseData.video_url,
            hasThumbnail: !!courseData.thumbnail
          });
        } else {
          // More specific error handling
          if (response.status === 404) {
            setError("Course not found. The course may have been deleted or doesn't exist.");
          } else if (response.status === 503) {
            setError("Network error: Please check your internet connection and try again.");
          } else {
            setError(response.message || "Failed to load course details. Please try again later.");
          }
        }
      } catch (err) {
        console.error("Error fetching course:", err);
        // More specific error message for network issues
        if (err instanceof TypeError && err.message.includes('fetch')) {
          setError("Network error: Please check your internet connection and try again.");
        } else {
          setError("An unexpected error occurred while loading the course. Please try again later.");
        }
      } finally {
        setLoading(false)
      }
    }

    fetchCourse()
  }, [id])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (previewTimeout) {
        clearTimeout(previewTimeout)
      }
    }
  }, [previewTimeout])

  // Fetch members list (lazily, the first time the tab is opened)
  useEffect(() => {
    const fetchMembers = async () => {
      if (activeTab !== "Members list" || membersLoaded || !id) return
      setMembersLoading(true)
      setMembersError(null)
      try {
        const response = await getCourseMembers(id)
        if (response.status === 200) {
          setMembers(response.data || [])
          setMemberStats({
            totalMembers: response.totalMembers || 0,
            totalWatched: response.totalWatched || 0,
          })
          setMembersLoaded(true)
        } else {
          setMembersError(response.message || "Failed to load members")
        }
      } catch (err) {
        console.error("Error fetching course members:", err)
        setMembersError("An unexpected error occurred while loading members")
      } finally {
        setMembersLoading(false)
      }
    }

    fetchMembers()
  }, [activeTab, id, membersLoaded])

  const truncateDescription = (text: string, maxLength: number = 50) => {
    if (!text) return ""
    const words = text.split(' ')
    if (words.length > maxLength) {
      return words.slice(0, maxLength).join(' ') + '...'
    }
    return text
  }

  // Get auth token for URLs
  const authToken = localStorage.getItem("AUTH_TOKEN")
  const videoSrc = course?.videoUrl ? 
    (course.videoUrl.startsWith('http') ? `${course.videoUrl}?token=${authToken}` : course.videoUrl) 
    : ""
    
  const thumbnailSrc = course?.thumbnail ? 
    (course.thumbnail.startsWith('http') ? `${course.thumbnail}?token=${authToken}` : course.thumbnail) 
    : ""

  // Function to handle closing video player
  const handleCloseVideoPlayer = () => {
    setShowVideoPlayer(false)
  }

  // YouTube-style preview functions
  const handleMouseEnter = () => {
    if (mediaType === 'video' && videoSrc) {
      const timeout = setTimeout(() => {
        setShowPreview(true)
      }, 1000) // 1 second delay like YouTube
      setPreviewTimeout(timeout)
    }
  }

  const handleMouseLeave = () => {
    if (previewTimeout) {
      clearTimeout(previewTimeout)
      setPreviewTimeout(null)
    }
    setShowPreview(false)
  }

  // Function to get status color classes
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft": return "text-blue-600 bg-blue-50 border-blue-200"
      case "Published": return "text-green-600 bg-green-50 border-green-200"
      case "Sold out": return "text-orange-600 bg-orange-50 border-orange-200"
      case "Selling": return "text-emerald-600 bg-emerald-50 border-emerald-200"
      default: return "text-gray-600 bg-gray-50 border-gray-200"
    }
  }

  // Function to handle status update
  const handleStatusUpdate = async (courseId: string, newStatus: string) => {
    setIsUpdatingStatus(true)
    try {
      const response = await updateCourseStatus(courseId, newStatus, localStorage.getItem("USER_ID") || "");
      
      if (response.status === 200) {
        setCourse((prev: any) => ({ ...prev, status: newStatus }));
        alert('Course status updated successfully!');
      } else {
        alert(`Failed to update status: ${response.message}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('An error occurred while updating the status');
    } finally {
      setIsUpdatingStatus(false)
      setShowStatusPopup(false)
    }
  }

  const filteredMembers = useMemo(() => {
    const q = memberSearch.toLowerCase()
    if (!q) return members
    return members.filter(m =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q) ||
      (m.phone || "").toLowerCase().includes(q)
    )
  }, [members, memberSearch])

  const totalMemberPages = Math.max(1, Math.ceil(filteredMembers.length / memberPageSize))
  const paginatedMembers = filteredMembers.slice((memberPage - 1) * memberPageSize, memberPage * memberPageSize)

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      </DashboardLayout>
    )
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-red-500">{error}</p>
          <Button 
            onClick={() => navigate("/courses")} 
            className="mt-4"
          >
            Back to courses
          </Button>
        </div>
      </DashboardLayout>
    )
  }
  
  if (!course) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Course not found.</p>
          <Button 
            onClick={() => navigate("/courses")} 
            className="mt-4"
          >
            Back to courses
          </Button>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" style={{padding: "0 1rem"}}>
        {/* Top Bar */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <button 
            onClick={() => navigate("/courses")} 
            className="flex items-center text-gray-800 hover:text-gray-900 text-sm mt-4 ml-2"
          >
            <ArrowLeft className="mr-2" size={18} />
            Back to Courses
          </button>
        </div>
        
        {/* Course Header with Enhanced Thumbnail/Video */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Enhanced Media section */}
            <div className="w-full lg:w-2/5">
              <div className="relative aspect-[16/9] group">
                {showVideoPlayer && videoSrc ? (
                  <div className="relative w-full h-full">
                    <VideoShowPlayer 
                      src={videoSrc} 
                      onClose={handleCloseVideoPlayer}
                      autoPlay={true}
                    />
                  </div>
                ) : mediaType === 'image' ? (
                  <div className="w-full h-full relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-600/10 z-10"></div>
                    <img
                      src={thumbnailSrc}
                      alt={course.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        console.log("Image failed to load:", thumbnailSrc)
                        const target = e.target as HTMLImageElement
                        target.src = "https://via.placeholder.com/400x300/6366f1/white?text=Course+Image"
                      }}
                      onLoad={() => {
                        //console.log("Image loaded successfully:", thumbnailSrc)
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-20"></div>
                  </div>
                ) : mediaType === 'video' ? (
                  <div 
                    className="relative w-full h-full cursor-pointer"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-600/10 z-10"></div>
                    
                    {/* Main thumbnail */}
                    <img
                      src={thumbnailSrc}
                      alt={course.title}
                      className={`w-full h-full object-cover transition-all duration-500 ${
                        showPreview ? 'opacity-0' : 'opacity-100 group-hover:scale-105'
                      }`}
                      onError={(e) => {
                        console.log("Video thumbnail failed to load:", thumbnailSrc)
                        const target = e.target as HTMLImageElement
                        target.src = "https://via.placeholder.com/400x300/6366f1/white?text=Video+Preview"
                      }}
                      onLoad={() => {
                        console.log("Video thumbnail loaded successfully:", thumbnailSrc)
                      }}
                    />
                    
                    {/* YouTube-style video preview */}
                    {showPreview && videoSrc && (
                      <div className="absolute inset-0 z-15">
                        <video
                          src={videoSrc}
                          className="w-full h-full object-cover"
                          autoPlay
                          muted
                          loop
                          playsInline
                          onError={() => {
                            console.log("Video preview failed to load")
                            setShowPreview(false)
                          }}
                        />
                      </div>
                    )}
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-20"></div>
                    
                    {/* Play button overlay */}
                    <button 
                      onClick={() => setShowVideoPlayer(true)}
                      className="absolute inset-0 flex items-center justify-center z-30 group/play"
                    >
                      <div className={`bg-white/90 backdrop-blur-sm rounded-full p-3 sm:p-4 shadow-xl transition-all duration-300 group-hover/play:bg-white group-hover/play:scale-110 ${
                        showPreview ? 'opacity-80' : 'opacity-100'
                      }`}>
                        <Play className="text-purple-600 ml-0.5 sm:ml-1" size={24} />
                      </div>
                    </button>
                    
                    {/* YouTube-style preview indicator */}
                    {showPreview && (
                      <div className="absolute top-2 sm:top-4 left-2 sm:left-4 z-35">
                        <div className="bg-black/70 text-white text-xs px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                          Preview
                        </div>
                      </div>
                    )}
                    
                    {/* Duration badge (optional) */}
                    <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 z-35">
                      <div className="bg-black/80 text-white text-xs px-1.5 py-0.5 sm:px-2 sm:py-1 rounded">
                        {/* You can add video duration here if available */}
                        Video
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center p-4">
                    <div className="bg-white rounded-full p-4 sm:p-6 shadow-lg mb-4">
                      <Image className="text-gray-400" size={32} />
                    </div>
                    <span className="text-gray-500 font-medium text-center text-sm">No thumbnail available</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Enhanced Course info section */}
            <div className="flex-1 p-4 sm:p-5 lg:p-4">
              <div className="flex flex-col sm:flex-row justify-between items-start mb-4 sm:mb-6 gap-4">
                <div className="flex-1">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight mb-1">{course.title}</h1>
                  <p className="text-gray-600 font-medium text-sm">by {course.instructor}</p>
                </div>
                {/* Enhanced Status badge */}
                <button 
                  onClick={() => setShowStatusPopup(true)}
                  className={`inline-flex items-center px-3 py-2 sm:px-4 rounded-full text-xs font-semibold border-2 shadow-md transition-all duration-300 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${getStatusColor(course.status)} hover:bg-opacity-90 cursor-pointer whitespace-nowrap`}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-current mr-1 opacity-75"></div>
                  {course.status}
                  <ChevronDown className="ml-1 transition-transform duration-200" size={14} />
                </button>
              </div>
              
<div className="course-details-description text-gray-700 leading-relaxed mb-4">

  <div className="course-description-collapsed">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>
      {course.description || ""}
    </ReactMarkdown>
  </div>

  {course.description.split(/\s+/).length > 30 && (
    <button
      onClick={() => setShowDescriptionModal(true)}
      className="text-purple-600 hover:text-purple-700 font-semibold mt-2 inline-flex items-center transition-colors duration-200 text-xs"
    >
      Read more
    </button>
  )}

</div>           
              
              {/* Enhanced Action buttons */}
              <div className="flex flex-wrap items-center gap-2">
                <button 
                  className="inline-flex items-center px-3 py-2 sm:px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-blue-700 hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-xs"
                  onClick={() => setShowShareModal(true)}
                >
                  <Share2 className="mr-1" size={14} />
                  <span className="hidden xs:inline">Share</span>
                  <span className="xs:hidden">Share</span>
                </button>
                <button 
                  className="inline-flex items-center px-3 py-2 sm:px-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-lg shadow-md hover:from-emerald-600 hover:to-emerald-700 hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 text-xs"
                  onClick={() => window.open(`/courses/landing/${course.id}`, '_blank')}
                >
                  <Eye className="mr-1" size={14} />
                  <span className="hidden xs:inline">View landing</span>
                  <span className="xs:hidden">View</span>
                </button>
                <button 
                  className="inline-flex items-center px-3 py-2 sm:px-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-lg shadow-md hover:from-purple-600 hover:to-purple-700 hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500/30 text-xs"
                  onClick={() => navigate(`/courses/edit/${course.id}`)}
                >
                  <Edit2 className="mr-1" size={14} />
                  <span className="hidden xs:inline">Edit</span>
                  <span className="xs:hidden">Edit</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Enhanced Tabs */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-1">
          <div className="flex flex-wrap gap-1">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${
                  activeTab === tab 
                    ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md" 
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
        
        {/* Tab Content */}
        {activeTab === "Overview" && (
          <div className="space-y-6">
            {/* Enhanced Overview Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl shadow p-3 text-center">
                <div className="bg-emerald-500 rounded-full p-2 w-fit mx-auto mb-2">
                  <DollarSign className="text-white" size={16} />
                </div>
                <p className="text-base font-bold text-emerald-800 mb-1">{course.metrics.totalEarnings}</p>
                <p className="text-xs font-medium text-emerald-700">Total earnings</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl shadow p-3 text-center">
                <div className="bg-blue-500 rounded-full p-2 w-fit mx-auto mb-2">
                  <Users className="text-white" size={16} />
                </div>
                <p className="text-base font-bold text-blue-800 mb-1">{course.metrics.purchasedUsers}</p>
                <p className="text-xs font-medium text-blue-700">Purchased users</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl shadow p-3 text-center">
                <div className="bg-purple-500 rounded-full p-2 w-fit mx-auto mb-2">
                  <CheckCircle className="text-white" size={16} />
                </div>
                <p className="text-base font-bold text-purple-800 mb-1">{course.metrics.courseCompleted}</p>
                <p className="text-xs font-medium text-purple-700">Course completed</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl shadow p-3 text-center">
                <div className="bg-orange-500 rounded-full p-2 w-fit mx-auto mb-2">
                  <Percent className="text-white" size={16} />
                </div>
                <p className="text-base font-bold text-orange-800 mb-1">{course.metrics.avgPurchaseRate}</p>
                <p className="text-xs font-medium text-orange-700">Avg Purchase Rate</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === "Members list" && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl shadow p-3 text-center">
                <div className="bg-blue-500 rounded-full p-2 w-fit mx-auto mb-2">
                  <Users className="text-white" size={16} />
                </div>
                <p className="text-base font-bold text-blue-800 mb-1">{memberStats.totalMembers}</p>
                <p className="text-xs font-medium text-blue-700">Members with access</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl shadow p-3 text-center">
                <div className="bg-emerald-500 rounded-full p-2 w-fit mx-auto mb-2">
                  <PlayCircle className="text-white" size={16} />
                </div>
                <p className="text-base font-bold text-emerald-800 mb-1">{memberStats.totalWatched}</p>
                <p className="text-xs font-medium text-emerald-700">Have watched</p>
              </div>
            </div>

            {membersLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : membersError ? (
              <div className="text-center py-12 text-red-500 text-sm">{membersError}</div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
                {/* Filter bar */}
                <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none text-gray-400" size={15} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                      type="text"
                      placeholder="Search by name, email, phone…"
                      value={memberSearch}
                      onChange={e => { setMemberSearch(e.target.value); setMemberPage(1); }}
                      style={{ height: '42px', paddingLeft: '36px', margin:"0px" }}
                      className="w-full pr-4 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm text-gray-500 font-medium">Rows:</span>
                    <select
                      value={memberPageSize}
                      onChange={e => { setMemberPageSize(Number(e.target.value)); setMemberPage(1); }}
                      style={{ height: '42px', margin: '0px' }}
                      className="text-sm border border-gray-200 rounded-xl px-2 focus:outline-none focus:ring-2 focus:ring-purple-300 bg-white"
                    >
                      {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ minWidth: "900px" }}>
                    <thead>
                      <tr style={{ background: "linear-gradient(135deg, #3b0764 0%, #4c1d95 40%, #5b21b6 100%)" }}>
                        <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">#</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Email</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Phone</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Access</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Purchased on</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Progress</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Watched</th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-black uppercase tracking-wider">Certificate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paginatedMembers.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center py-12 text-gray-400 text-sm">
                            {memberSearch ? "No members match your search." : "No members have access to this course yet."}
                          </td>
                        </tr>
                      ) : paginatedMembers.map((member, idx) => {
                       
                        const rowNum = (memberPage - 1) * memberPageSize + idx + 1
                        return (
                          <tr key={member.userId} className="hover:bg-purple-50/30 transition-colors">
                            <td className="px-4 py-3 text-gray-400 text-xs" style={{ whiteSpace: "nowrap" }}>{rowNum}</td>
                            <td className="px-4 py-3" style={{ whiteSpace: "nowrap" }}>
                              <div className="flex items-center gap-2">
                                
                                <span className="font-medium text-gray-900">{member.name || "Unknown"}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600" style={{ whiteSpace: "nowrap" }}>{member.email || "—"}</td>
                            <td className="px-4 py-3 text-gray-600" style={{ whiteSpace: "nowrap" }}>{member.phone || "—"}</td>
                            <td className="px-4 py-3" style={{ whiteSpace: "nowrap" }}>
                              {member.accessType === "Channel" ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                  <Radio size={11} /> Channel
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                                  <ShoppingBag size={11} /> Purchased
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-600 text-xs" style={{ whiteSpace: "nowrap" }}>
                              {member.purchasedAt ? new Date(member.purchasedAt).toLocaleDateString() : "—"}
                            </td>
                            <td className="px-4 py-3" style={{ whiteSpace: "nowrap" }}>
                              <div className="flex items-center gap-2">
                                <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div className="h-full bg-purple-500" style={{ width: `${Math.min(member.courseViewPercent || 0, 100)}%` }} />
                                </div>
                                <span className="text-xs text-gray-600">{(member.courseViewPercent || 0).toFixed(0)}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-3" style={{ whiteSpace: "nowrap" }}>
                              {member.hasWatched ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <Eye size={11} /> Yes
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3" style={{ whiteSpace: "nowrap" }}>
                              {member.certificateSent ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                                  <Award size={11} /> Sent
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderTop: '1px solid #f3f4f6', flexWrap: 'wrap', gap: '10px' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                    Showing{' '}
                    <strong style={{ color: '#374151' }}>
                      {filteredMembers.length === 0 ? 0 : (memberPage - 1) * memberPageSize + 1}–{Math.min(memberPage * memberPageSize, filteredMembers.length)}
                    </strong>
                    {' '}of{' '}
                    <strong style={{ color: '#374151' }}>{filteredMembers.length}</strong> members
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setMemberPage(p => Math.max(1, p - 1))}
                      disabled={memberPage === 1}
                      style={{
                        padding: '7px 10px', borderRadius: '8px',
                        border: '1.5px solid #e5e7eb', background: '#fff',
                        color: memberPage === 1 ? '#d1d5db' : '#374151',
                        cursor: memberPage === 1 ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center',
                      }}
                    >
                      <ChevronLeft size={15} />
                    </button>

                    {Array.from({ length: totalMemberPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalMemberPages || Math.abs(p - memberPage) <= 1)
                      .reduce<(number | '...')[]>((acc, p, i, arr) => {
                        if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('...')
                        acc.push(p)
                        return acc
                      }, [])
                      .map((p, i) =>
                        p === '...' ? (
                          <span key={`e${i}`} style={{ padding: '0 4px', color: '#9ca3af', fontSize: '13px' }}>…</span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setMemberPage(p as number)}
                            style={{
                              width: '34px', height: '34px', borderRadius: '8px',
                              border: memberPage === p ? 'none' : '1.5px solid #e5e7eb',
                              background: memberPage === p ? '#7c3aed' : '#fff',
                              color: memberPage === p ? '#fff' : '#374151',
                              cursor: 'pointer',
                              fontWeight: memberPage === p ? 700 : 400,
                              fontSize: '13px',
                              boxShadow: memberPage === p ? '0 2px 8px rgba(124,58,237,0.35)' : 'none',
                            }}
                          >
                            {p}
                          </button>
                        )
                      )
                    }

                    <button
                      onClick={() => setMemberPage(p => Math.min(totalMemberPages, p + 1))}
                      disabled={memberPage === totalMemberPages}
                      style={{
                        padding: '7px 10px', borderRadius: '8px',
                        border: '1.5px solid #e5e7eb', background: '#fff',
                        color: memberPage === totalMemberPages ? '#d1d5db' : '#374151',
                        cursor: memberPage === totalMemberPages ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center',
                      }}
                    >
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Status Update Popup */}
      <StatusUpdatePopup
        isOpen={showStatusPopup}
        onClose={() => setShowStatusPopup(false)}
        currentStatus={course.status}
        courseId={course.id}
        onUpdate={handleStatusUpdate}
      />
      
      {/* Enhanced Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-md shadow-xl transform transition-all duration-300">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Share Your Course</h3>
                <p className="text-gray-600 text-sm">Share the active payment links with your audiences</p>
              </div>
              <button 
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 hover:bg-gray-100 rounded-full"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">💳 Payment link</label>
                <div className="flex rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                  <input
                    type="text"
                    value={`${window.location.origin}/payment/course/${course.id}`}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm bg-gray-50 border-0 focus:outline-none"
                  />
                  <button 
                    className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 text-sm font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/payment/course/${course.id}`);
                      // Add a subtle feedback here if needed
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">🌐 Landing page link</label>
                <div className="flex rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                  <input
                    type="text"
                    value={`${window.location.origin}/courses/landing/${course.id}`}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm bg-gray-50 border-0 focus:outline-none"
                  />
                  <button 
                    className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-4 text-sm font-medium hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:ring-offset-1"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/courses/landing/${course.id}`);
                      // Add a subtle feedback here if needed
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">🛒 Checkout page link</label>
                <div className="flex rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                  <input
                    type="text"
                    value={`${window.location.origin}/checkout/course/${course.id}`}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm bg-gray-50 border-0 focus:outline-none"
                  />
                  <button 
                    className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 text-sm font-medium hover:from-purple-600 hover:to-purple-700 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:ring-offset-1"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/checkout/course/${course.id}`);
                      // Add a subtle feedback here if needed
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-semibold transition-colors duration-200 text-sm"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

{/* Full Description Modal */}
{showDescriptionModal && (
  <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] shadow-2xl flex flex-col -translate-y-8">

      {/* Modal Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            {course.title}
          </h3>          
        </div>

        <button
          onClick={() => setShowDescriptionModal(false)}
          className="text-gray-400 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <span className="text-xl leading-none">×</span>
        </button>
      </div>

      {/* Description */}
      <div className="px-6 py-5 overflow-y-auto">
        <div className="course-details-description text-gray-700 leading-relaxed">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {course.description || ""}
          </ReactMarkdown>
        </div>
      </div>

      {/* Modal Footer */}
      <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
        <button
          onClick={() => setShowDescriptionModal(false)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
        >
          Close
        </button>
      </div>

    </div>
  </div>
)}      
    </DashboardLayout>
  )
}

export default CourseDetails